import { Injectable } from '@angular/core';
import { Router, CanActivate } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SETORES_HOSPITALARES_PERMISSION_KEY } from '../constants/setores-hospitalares-menu.constants';
import { UserAccessService } from '../services/user-access.service';
import { NotificationService } from '../services/notification.service';
import { I18nService } from '../services/i18n.service';

/**
 * Gate of the `/hospital-sectors` page. Checks the PERMISSION key
 * (`SETORES_HOSPITALARES`), never the submodule key (`cadastro_setores_hospitalares`),
 * which only identifies the menu leaf.
 *
 * Declared on the child route (`path: ''`) so it runs one level BELOW `AuthGuard`: guards of
 * the same `canActivate` array run concurrently, and the denial toast would fire even while
 * `AuthGuard` is already refusing the navigation.
 */
@Injectable({
  providedIn: 'root'
})
export class SetoresHospitalaresGuard implements CanActivate {

  constructor(
    private router: Router,
    private userAccess: UserAccessService,
    private notificationService: NotificationService,
    private i18n: I18nService
  ) {}

  canActivate(): Observable<boolean> {
    // `skipCache`: the permission can be granted mid-session and the service memoizes per
    // session, so a cached `false` would keep denying the route until the next login.
    return this.userAccess.check(SETORES_HOSPITALARES_PERMISSION_KEY, { skipCache: true }).pipe(
      tap(canAccess => {
        if (!canAccess) {
          this.denyAccess();
        }
      })
    );
  }

  private denyAccess(): void {
    this.notificationService.warning(
      this.i18n.translate('notifications.moduleSetoresHospitalares'),
      this.i18n.translate('notifications.accessDenied')
    );

    const cameFromWithinApp = !!this.router.getCurrentNavigation()?.previousNavigation;
    if (!cameFromWithinApp) {
      this.router.navigate(['/home']);
    }
  }
}
